import React, {useState, useEffect, useCallback} from 'react';
import {getConnectionStatus} from '../../services/socketService';
import {Form, Input, Button, Row, Col, Space, Typography, Modal, Alert, Tooltip} from 'antd';
import {MaskedInput} from 'antd-mask-input';
import {useMediaQuery} from 'react-responsive';
import {motion} from 'framer-motion';
import {useSelector} from 'react-redux';
import {CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, QuestionCircleOutlined} from '@ant-design/icons';
import promoterService from '../../services/PromoterService';
import {store} from '../../store/store';
import theme from "../../theme";
import zipCodeRangeService from "../../services/ZipCodeRangeService";
import electorKeyValidator from "../../services/ElectorKeyValidator";
import {addPendingPromoterForm} from "../../store/reducers/formPromoterReducer";
import goalService from "../../services/GoalService";
import {PROMOTER_REGISTRATION_ENABLED} from "../../utils/featureFlags";

const {Title, Text} = Typography;

const PromoterRegisterForm = ({selectedSection, onSuccess}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalType, setModalType] = useState(null);
    const [modalMessage, setModalMessage] = useState('');
    const [connectionStatus, setConnectionStatus] = useState(getConnectionStatus());
    const [zipCodeRange, setZipCodeRange] = useState({});
    const isMobile = useMediaQuery({maxWidth: 800});
    const auth = useSelector((state) => state.auth || {});
    const entityId = auth.user?.entityId || 0;
    const municipalityId = auth.user?.municipalityId || 0;
    const districtId = auth.user?.districtId || 0;
    const section = useSelector((state) => state.section);
    const [modal, contextHolder] = Modal.useModal();

    useEffect(() => {
        const interval = setInterval(() => {
            setConnectionStatus(getConnectionStatus());
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const fetchZipCodeRange = async () => {
            try {
                const filters = {
                    entityId: {value: entityId, operator: "eq"},
                    municipalityId: {value: municipalityId, operator: "eq"},
                };
                const responseZipCodeRange = await zipCodeRangeService.getAll(filters);
                if (responseZipCodeRange.data.length > 0) {
                    setZipCodeRange(responseZipCodeRange.data[0]);
                }
            } catch (error) {
                console.error("Error fetching zip code data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchZipCodeRange().catch(console.error);
    }, [entityId, municipalityId]);

    const handleSubmit = useCallback(async (values) => {
        setLoading(true);

        const formData = {
            ...values,
            phone: values.phone.replace(/\D/g, ''),
            entityId,
            municipalityId,
            districtId,
            sectionId: parseInt(values.sectionId.toString()),
            interiorNumber: !values.interiorNumber || values.interiorNumber.trim() === '' ? null : values.interiorNumber,
        };

        if (getConnectionStatus()) {
            try {
                const filters = {
                    entityId: { value: formData.entityId, operator: "eq" },
                    districtId: { value: formData.districtId, operator: "eq" },
                    sectionId: { value: formData.sectionId, operator: "in" },
                };
                // const { data: goals } = await goalService.getAll(filters);
                // const { meta: metaPromoters } = await promoterService.getAll(filters);
                // if (goals.length === 1 && goals[0].promotersLimit > metaPromoters.totalItems) {
                    await promoterService.create(formData);
                    form.resetFields();
                    showModal('success', 'Registro de promotor exitoso.');
                    if (onSuccess) {
                        onSuccess();
                    }
                // } else {
                //     modal.warning({
                //         title: 'Límite de promotores',
                //         content: 'No está permitido agregar más promotores',
                //     });
                // }
            } catch (error) {
                if (error.errorCode === "VALIDATION_ERROR" || error.errorCode === "BAD_REQUEST") {
                    const validationErrors = error.details;
                    const errorMessages = validationErrors.flatMap(errorObj => Object.values(errorObj));
                    showModal('error', (
                        <>
                            <p><b>Por favor corrige los siguientes errores:</b></p>
                            <ul style={{textAlign: "left"}}>
                                {errorMessages.map((msg, index) => (
                                    <li key={index}>{msg}</li>
                                ))}
                            </ul>
                        </>
                    ));
                } else {
                    showModal('error', error.message || 'Error al enviar el formulario.');
                }
            }
        } else {
            form.resetFields();
            formData['user'] = auth.user.username;
            store.dispatch(addPendingPromoterForm(formData));
            showModal('offline', 'El formulario será enviado cuando haya conexión.');
            onSuccess();
        }

        setLoading(false);
    }, [entityId, municipalityId, districtId, form, onSuccess, modal, auth.user.username]);

    const showModal = (type, message) => {
        setModalType(type);
        setModalMessage(message);
        setModalVisible(true);
    };

    if (!PROMOTER_REGISTRATION_ENABLED) {
        return (
            <div style={{padding: "16px", background: theme.colors.card, borderRadius: "8px"}}>
                <Title level={2} style={{textAlign: "center", marginBottom: "20px"}}>Registro de Promotor</Title>
                <Alert
                    message="Registro de promotores cerrado"
                    description="Ya no es posible registrar nuevos promotores."
                    type="warning"
                    showIcon
                />
            </div>
        );
    }

    return (
        <>
            {contextHolder}
            <div
                style={{
                    padding: "16px",
                    background: theme.colors.card,
                    borderRadius: "8px",
                }}
            >
                <Title level={2} style={{textAlign: "center", marginBottom: "20px"}}>Registro de Promotor</Title>

                {!connectionStatus && (
                    <Alert
                        message="Modo sin conexión a internet"
                        description="Los registros se guardarán localmente y se sincronizarán automáticamente cuando vuelvas a tener conexión."
                        type="warning"
                        showIcon
                        style={{marginBottom: '16px'}}
                    />
                )}

                <motion.div
                    initial={{opacity: 0.5}}
                    animate={{opacity: 1}}
                    transition={{duration: 0.5, repeat: Infinity, repeatType: "reverse"}}
                    style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'}}
                >
                    {connectionStatus ? (
                        <>
                            <CheckCircleOutlined style={{color: '#52c41a', fontSize: '16px'}}/>
                            <Text strong style={{color: '#52c41a'}}>Conectado a internet</Text>
                        </>
                    ) : (
                        <>
                            <CloseCircleOutlined style={{color: '#f5222d', fontSize: '16px'}}/>
                            <Text strong style={{color: '#f5222d'}}>Sin conexión a internet</Text>
                        </>
                    )}
                </motion.div>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    requiredMark="optional"
                    style={{marginTop: "20px", textAlign: "left"}}
                >
                    <Form.Item
                        label="Clave de Elector *"
                        name="electorKey"
                        rules={[
                            {
                                required: true,
                                validator: (_, value) =>
                                    electorKeyValidator.validate(value) ?
                                        Promise.resolve() :
                                        Promise.reject(new Error('Clave de elector inválida.')),
                            },
                        ]}
                    >
                        <Input placeholder="Ingrese la clave de elector" maxLength={18}/>
                    </Form.Item>

                    <Form.Item
                        label="Correo Electrónico *"
                        name="email"
                        rules={[
                            {required: true, message: 'El correo electrónico es obligatorio.'},
                            {type: 'email', message: 'Debe ser un correo electrónico válido.'}
                        ]}
                    >
                        <Input placeholder="Correo Electrónico"/>
                    </Form.Item>

                    <Form.Item
                        label="Teléfono *"
                        name="phone"
                        rules={[
                            {required: true, message: 'El teléfono es obligatorio.'},
                            {
                                validator: (_, value) => {
                                    const cleanedValue = value.replace(/\D/g, '');
                                    if (cleanedValue.length !== 10) {
                                        return Promise.reject(new Error('Debe contener exactamente 10 dígitos.'));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <MaskedInput mask="000-000-0000" placeholder="555-123-4567"/>
                    </Form.Item>

                    {/* Se mantienen los demás campos: name, firstLastName, secondLastName, etc. */}
                    <Row gutter={16}>
                        <Col span={isMobile ? 24 : 8}>
                            <Form.Item
                                label="Nombre *"
                                name="name"
                                rules={[{required: true, message: 'El nombre es obligatorio.'}]}
                            >
                                <Input placeholder="Nombre"/>
                            </Form.Item>
                        </Col>
                        <Col span={isMobile ? 24 : 8}>
                            <Form.Item
                                label="Apellido Paterno *"
                                name="firstLastName"
                                rules={[{required: true, message: 'El apellido paterno es obligatorio.'}]}
                            >
                                <Input placeholder="Apellido Paterno"/>
                            </Form.Item>
                        </Col>
                        <Col span={isMobile ? 24 : 8}>
                            <Form.Item 
                                label={
                                    <span>
                                        Apellido Materno (o XX){' '}
                                        <Tooltip title="En caso de no contar con segundo apellido debe escribir XX">
                                            <QuestionCircleOutlined style={{color: '#9f1717'}} />
                                        </Tooltip>
                                    </span>
                                }
                                name="secondLastName"
                                rules={[{required: true, message: 'El apellido materno es obligatorio (o XX si no tiene).'}]}
                            >
                                <Input placeholder="Apellido Materno"/>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Calle *"
                        name="street"
                        rules={[{required: true, message: 'La calle es obligatoria.'}]}
                    >
                        <Input placeholder="Ingrese la calle"/>
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={isMobile ? 24 : 12}>
                            <Form.Item
                                label="Número Exterior *"
                                name="exteriorNumber"
                                rules={[{required: true, message: 'El número exterior es obligatorio.'}]}
                            >
                                <Input placeholder="Número Exterior"/>
                            </Form.Item>
                        </Col>
                        <Col span={isMobile ? 24 : 12}>
                            <Form.Item
                                label="Número Interior"
                                name="interiorNumber"
                            >
                                <Input placeholder="Número Interior (Opcional)"/>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={isMobile ? 24 : 8}>
                            {/* TEMPORAL: Validación de rango de código postal deshabilitada temporalmente */}
                            <Form.Item
                                label="Código Postal *"
                                name="zipCode"
                                rules={[
                                    {required: true, message: 'El código postal es obligatorio.'},
                                    {
                                        pattern: /^\d{5}$/,
                                        message: 'Debe contener exactamente 5 dígitos numéricos.'
                                    },
                                ]}
                            >
                                <Input
                                    placeholder="Código Postal"
                                    maxLength={5}
                                    onInput={(e) => {
                                        e.target.value = e.target.value.replace(/\D/g, '');
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={isMobile ? 24 : 8}>
                            <Form.Item
                                label="Colonia *"
                                name="neighborhood"
                                rules={[{required: true, message: 'Este campo es obligatorio.'}]}
                            >
                                <Input placeholder="Colonia"/>
                            </Form.Item>
                        </Col>
                        <Col span={isMobile ? 24 : 8}>
                            <Form.Item
                                label="Municipio/Delegación *"
                                name="municipalityName"
                                initialValue={section.municipalityName}
                                rules={[{required: true, message: 'Este campo es obligatorio.'}]}
                            >
                                <Input placeholder="Municipio/Delegación" disabled={true}/>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Estado *"
                        name="entityName"
                        initialValue={section.entityName}
                        rules={[{required: true, message: 'El estado es obligatorio.'}]}
                    >
                        <Input placeholder="Estado" disabled={true}/>
                    </Form.Item>

                    <Form.Item
                        label="Sección *"
                        name="sectionId"
                        initialValue={selectedSection}
                        rules={[{required: true, message: 'Debe seleccionar una sección.'}]}
                    >
                        <Input value={`Sección ${selectedSection}`} disabled={true}/>
                    </Form.Item>

                    <Form.Item>
                        <Space style={{width: '100%', justifyContent: 'right'}}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                disabled={loading}
                                style={{
                                    backgroundColor: connectionStatus ? '#52c41a' : '#f5222d',
                                    borderColor: connectionStatus ? '#52c41a' : '#f5222d'
                                }}
                            >
                                {connectionStatus ? "Enviar registro" : "Guardar registro"}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>

                <Modal
                    open={modalVisible}
                    footer={null}
                    closable={false}
                    centered
                    onCancel={() => setModalVisible(false)}
                >
                    {modalType === 'success' && (
                        <div style={{textAlign: 'center'}}>
                            <CheckCircleOutlined style={{color: '#52c41a', fontSize: 48}}/>
                            <Title level={3}>Registro Exitoso</Title>
                            <Text>{modalMessage}</Text>
                        </div>
                    )}
                    {modalType === 'error' && (
                        <div style={{textAlign: 'center'}}>
                            <CloseCircleOutlined style={{color: '#f5222d', fontSize: 48}}/>
                            <Title level={3}>Error</Title>
                            <Text>{modalMessage}</Text>
                        </div>
                    )}
                    {modalType === 'offline' && (
                        <div style={{textAlign: 'center'}}>
                            <ExclamationCircleOutlined style={{color: '#faad14', fontSize: 48}}/>
                            <Title level={3}>Modo Offline</Title>
                            <Text>{modalMessage}</Text>
                        </div>
                    )}
                </Modal>
            </div>
        </>
    );
};

export default PromoterRegisterForm;